import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  PublicationInboxDetail,
} from '@/hooks/usePublicationInboxDetail';
import type {
  PublicationInboxItem,
} from '@/hooks/usePublicationInbox';
import {
  usePublicationInboxActions,
  type PublicationInboxReviewContext,
} from '@/hooks/usePublicationInboxActions';

import {
  buildPublicationInboxCorrectedAnalysis,
  publicationInboxCandidateToDraft,
  publicationInboxReviewCandidateEquals,
  publicationInboxReviewResultMessage,
  type PublicationInboxCorrectionDraft,
  type PublicationInboxReviewDecision,
} from '@/lib/publicationInboxDecision';

type Props = {
  item:
    PublicationInboxItem | null;
  detail:
    PublicationInboxDetail | null;
  onChanged:
    () => Promise<void>;
};

const selectClass =
  'w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm text-foreground-700';

const inputClass =
  'w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm text-foreground-700';

const communicationOptions = [
  ['intimation', 'Intimação'],
  ['citation', 'Citação'],
  ['notification', 'Notificação'],
  ['unknown', 'Indefinida'],
] as const;

const subjectOptions = [
  ['expert_evidence', 'Prova pericial'],
  ['hearing', 'Audiência'],
  ['payment', 'Pagamento'],
  ['appellate_decision', 'Decisão recursal'],
  ['judgment', 'Sentença / julgamento'],
  ['decision', 'Decisão'],
  ['procedural_movement', 'Movimentação processual'],
  ['administrative', 'Administrativo'],
  ['other', 'Outro'],
] as const;

const eventOptions = [
  ['manifest_on_expert_report', 'Manifestar sobre laudo'],
  ['present_defense', 'Apresentar defesa'],
  ['present_counterarguments', 'Apresentar contrarrazões'],
  ['amend_initial', 'Emendar inicial'],
  ['pay_costs', 'Recolher custas'],
  ['attend_hearing', 'Comparecer à audiência'],
  ['provide_information', 'Prestar informações'],
  ['comply_order', 'Cumprir determinação'],
  ['withdraw_values', 'Levantar valores'],
  ['appeal', 'Recorrer'],
  ['take_notice_decision', 'Tomar ciência'],
  ['no_action', 'Sem providência'],
  ['other', 'Outro'],
] as const;

const actionTypeOptions = [
  ['petition', 'Petição'],
  ['review', 'Análise / revisão'],
  ['hearing', 'Audiência'],
  ['payment', 'Pagamento'],
  ['information', 'Informação'],
  ['appeal', 'Recurso'],
  ['other', 'Outra'],
  ['none', 'Nenhuma'],
] as const;

const operationTone = (
  value: string
) => {
  if (
    value ===
    'deadline_pending_confirmation'
  ) {
    return 'border-red-200 bg-red-50 text-red-800';
  }

  if (
    value === 'task_created' ||
    value === 'no_action'
  ) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }

  return 'border-amber-200 bg-amber-50 text-amber-800';
};

export default function PublicationInboxDecisionPanel({
  item,
  detail,
  onChanged,
}: Props) {
  const {
    loadReviewContext,
    submitReview,
    confirmDeadline,
  } = usePublicationInboxActions();

  const [
    reviewContext,
    setReviewContext,
  ] =
    useState<PublicationInboxReviewContext | null>(
      null
    );

  const [
    decision,
    setDecision,
  ] =
    useState<PublicationInboxReviewDecision>(
      'accepted'
    );

  const [
    draft,
    setDraft,
  ] =
    useState<PublicationInboxCorrectionDraft | null>(
      null
    );

  const [
    notes,
    setNotes,
  ] =
    useState('');

  const [
    dueDate,
    setDueDate,
  ] =
    useState('');

  const [
    deadlineChecked,
    setDeadlineChecked,
  ] =
    useState(false);

  const [
    isLoadingReview,
    setIsLoadingReview,
  ] =
    useState(false);

  const [
    isSaving,
    setIsSaving,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null
    );

  const [
    message,
    setMessage,
  ] =
    useState<string | null>(
      null
    );

  useEffect(() => {
    setReviewContext(null);
    setDraft(null);
    setNotes('');
    setError(null);
    setMessage(null);
    setDeadlineChecked(false);
    setDueDate(
      detail?.deadline?.dueDate ??
        ''
    );
  }, [
    item?.id,
    detail?.deadline?.id,
    detail?.deadline?.dueDate,
  ]);

  const state =
    item?.workflow.state ??
    null;

  const canOpenReview =
    Boolean(
      item &&
      item.context.caseId &&
      item.ai.runId &&
      (
        state ===
          'awaiting_review' ||
        state ===
          'decision_required'
      )
    );

  const baseCandidate =
    useMemo(
      () =>
        reviewContext
          ? (
              reviewContext
                .latestReviewedAnalysis ??
              reviewContext.candidate
            )
          : null,
      [reviewContext]
    );

  const updateDraft = <
    K extends keyof PublicationInboxCorrectionDraft
  >(
    key: K,
    value:
      PublicationInboxCorrectionDraft[K]
  ) => {
    setDraft(
      (current) =>
        current
          ? {
              ...current,
              [key]: value,
            }
          : current
    );
  };

  const openReview =
    async () => {
      if (!item) {
        return;
      }

      setIsLoadingReview(true);
      setError(null);
      setMessage(null);

      try {
        const context =
          await loadReviewContext(
            item.id
          );

        const base =
          context.latestReviewedAnalysis ??
          context.candidate;

        setReviewContext(
          context
        );

        setDraft(
          publicationInboxCandidateToDraft(
            base
          )
        );

        setDecision(
          context.latestReviewId &&
          context.latestReviewDecision !==
            'rejected'
            ? 'corrected'
            : 'accepted'
        );
      } catch (
        loadError
      ) {
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Não foi possível preparar a revisão.'
        );
      } finally {
        setIsLoadingReview(
          false
        );
      }
    };

  const saveReview =
    async () => {
      if (
        !item ||
        !reviewContext
      ) {
        return;
      }

      setIsSaving(true);
      setError(null);
      setMessage(null);

      try {
        if (
          decision ===
            'rejected' &&
          notes.trim().length <
            5
        ) {
          throw new Error(
            'Registre uma justificativa breve para rejeitar a análise da IA.'
          );
        }

        const correctionBase =
          baseCandidate ??
          reviewContext.candidate;

        const correctedAnalysis =
          decision ===
            'corrected'
            ? buildPublicationInboxCorrectedAnalysis(
                correctionBase,
                draft ??
                  publicationInboxCandidateToDraft(
                    correctionBase
                  )
              )
            : undefined;

        if (
          decision ===
            'corrected' &&
          correctedAnalysis &&
          publicationInboxReviewCandidateEquals(
            correctionBase,
            correctedAnalysis
          )
        ) {
          throw new Error(
            'Nenhuma alteração foi feita em relação à decisão humana atual.'
          );
        }

        const result =
          await submitReview({
            publicationId:
              item.id,
            analysisRunId:
              reviewContext
                .analysisRunId,
            decision,
            correctedAnalysis,
            notes,
            supersedesReviewId:
              reviewContext
                .latestReviewId,
          });

        setMessage(
          publicationInboxReviewResultMessage(
            decision,
            result.operationalizationState
          )
        );

        setReviewContext(
          null
        );

        setDraft(null);

        await onChanged();
      } catch (
        saveError
      ) {
        setError(
          saveError instanceof Error
            ? saveError.message
            : 'Não foi possível registrar a revisão.'
        );
      } finally {
        setIsSaving(false);
      }
    };

  const saveDeadline =
    async () => {
      if (
        !detail?.deadline?.id
      ) {
        setError(
          'O prazo operacional vinculado não foi encontrado.'
        );
        return;
      }

      if (
        !deadlineChecked
      ) {
        setError(
          'Confirme expressamente que a data foi conferida.'
        );
        return;
      }

      setIsSaving(true);
      setError(null);
      setMessage(null);

      try {
        await confirmDeadline(
          detail.deadline.id,
          dueDate
        );

        setMessage(
          'Data final confirmada. O sistema criará ou sincronizará a tarefa operacional vinculada.'
        );

        setDeadlineChecked(
          false
        );

        await onChanged();
      } catch (
        deadlineError
      ) {
        setError(
          deadlineError instanceof Error
            ? deadlineError.message
            : 'Não foi possível confirmar a data final.'
        );
      } finally {
        setIsSaving(false);
      }
    };

  if (!item) {
    return null;
  }

  const showDeadline =
    state ===
    'deadline_confirmation';

  const showReview =
    state ===
      'awaiting_review' ||
    state ===
      'decision_required';

  if (
    !showDeadline &&
    !showReview &&
    !message &&
    !error
  ) {
    return null;
  }

  return (
    <section className="space-y-3 rounded-2xl border border-primary-200 bg-primary-50/30 p-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-700">
          Decisão do advogado
        </p>
        <p className="mt-1 text-xs leading-5 text-foreground-600">
          A IA propõe. A decisão jurídica e a data final do prazo pertencem ao escritório.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs leading-5 text-red-700">
          {error}
        </div>
      )}

      {message && (
        <div
          className={`rounded-xl border p-3 text-xs leading-5 ${operationTone(
            item.operation.state ??
              ''
          )}`}
        >
          {message}
        </div>
      )}

      {showDeadline && (
        <div className="space-y-3 rounded-xl border border-red-200 bg-white p-3">
          <div className="flex items-center gap-2 text-red-800">
            <i className="ri-timer-flash-line"></i>
            <p className="text-sm font-semibold">
              Confirmar data final
            </p>
          </div>

          <p className="text-xs leading-5 text-red-700">
            Nenhuma data foi calculada ou confirmada pela IA. Informe a data final somente após conferir marco inicial, regra de contagem, feriados e eventual suspensão.
          </p>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-foreground-600">
              Data final conferida
            </span>
            <input
              type="date"
              value={dueDate}
              onChange={(event) =>
                setDueDate(
                  event.target.value
                )
              }
              className={inputClass}
            />
          </label>

          <label className="flex items-start gap-2 rounded-lg bg-red-50 p-2 text-xs leading-5 text-red-800">
            <input
              type="checkbox"
              checked={
                deadlineChecked
              }
              onChange={(event) =>
                setDeadlineChecked(
                  event.target
                    .checked
                )
              }
              className="mt-1"
            />
            <span>
              Confirmo que conferi juridicamente esta data. Ao confirmar, o fluxo canônico criará ou sincronizará a tarefa operacional.
            </span>
          </label>

          <button
            type="button"
            onClick={() =>
              void saveDeadline()
            }
            disabled={
              isSaving ||
              !dueDate ||
              !deadlineChecked
            }
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving
              ? 'Confirmando...'
              : 'Confirmar data final'}
          </button>
        </div>
      )}

      {showReview &&
        !item.context.caseId && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
            Vincule a publicação a um caso antes de operacionalizar a revisão. O vínculo continua disponível em “Administração e fluxo legado”.
          </div>
        )}

      {showReview &&
        item.context.caseId &&
        !item.ai.runId && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
            Não existe análise estruturada utilizável para revisão. A publicação continua ativa e deve ser tratada manualmente pelo fluxo administrativo legado, sem presunção automática de prazo.
          </div>
        )}

      {showReview &&
        canOpenReview &&
        !reviewContext && (
          <button
            type="button"
            onClick={() =>
              void openReview()
            }
            disabled={
              isLoadingReview ||
              isSaving
            }
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {isLoadingReview
              ? 'Carregando revisão...'
              : item.ai.reviewDecision
                ? 'Revisar novamente'
                : 'Revisar análise da IA'}
          </button>
        )}

      {reviewContext &&
        draft && (
          <div className="space-y-4 rounded-xl border border-background-200 bg-white p-3">
            <div className="rounded-lg bg-background-50 p-3 text-xs leading-5 text-foreground-700">
              <span className="font-semibold">
                Análise-base:
              </span>{' '}
              {baseCandidate
                ?.summary.concise ??
                reviewContext
                  .candidate
                  .summary.concise}
            </div>

            <div className="flex flex-wrap gap-2">
              {(
                [
                  [
                    'accepted',
                    'Aceitar IA',
                  ],
                  [
                    'corrected',
                    'Corrigir',
                  ],
                  [
                    'rejected',
                    'Rejeitar',
                  ],
                ] as const
              ).map(
                ([
                  value,
                  label,
                ]) => {
                  const hideAccepted =
                    value ===
                      'accepted' &&
                    Boolean(
                      reviewContext
                        .latestReviewId
                    ) &&
                    reviewContext
                      .latestReviewDecision !==
                      'rejected';

                  if (
                    hideAccepted
                  ) {
                    return null;
                  }

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setDecision(
                          value
                        )
                      }
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        decision ===
                        value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-background-200 text-foreground-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                }
              )}
            </div>

            {decision ===
              'corrected' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs leading-5 text-blue-800">
                  Os campos abaixo representam a decisão humana. Evidências, warnings e confiança da execução original são preservados como metadados, salvo o trecho de evidência do prazo que pode ser corrigido.
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Título jurídico
                    </span>
                    <input
                      value={
                        draft.summaryTitle
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'summaryTitle',
                          event.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Resumo
                    </span>
                    <textarea
                      value={
                        draft.summaryConcise
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'summaryConcise',
                          event.target
                            .value
                        )
                      }
                      rows={3}
                      className={
                        inputClass
                      }
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Contexto
                    </span>
                    <textarea
                      value={
                        draft.summaryContext
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'summaryContext',
                          event.target
                            .value
                        )
                      }
                      rows={2}
                      className={
                        inputClass
                      }
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Comunicação
                    </span>
                    <select
                      value={
                        draft.communication
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'communication',
                          event.target
                            .value as
                            typeof draft.communication
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      {communicationOptions.map(
                        ([
                          value,
                          label,
                        ]) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Assunto
                    </span>
                    <select
                      value={
                        draft.subject
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'subject',
                          event.target
                            .value as
                            typeof draft.subject
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      {subjectOptions.map(
                        ([
                          value,
                          label,
                        ]) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Evento
                    </span>
                    <select
                      value={
                        draft.event
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'event',
                          event.target
                            .value as
                            typeof draft.event
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      {eventOptions.map(
                        ([
                          value,
                          label,
                        ]) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Prioridade
                    </span>
                    <select
                      value={
                        draft.priority
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'priority',
                          event.target
                            .value as
                            typeof draft.priority
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      <option value="low">
                        Baixa
                      </option>
                      <option value="medium">
                        Média
                      </option>
                      <option value="high">
                        Alta
                      </option>
                      <option value="uncertain">
                        Incerta
                      </option>
                    </select>
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Exige ação?
                    </span>
                    <select
                      value={
                        draft.requiresAction
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'requiresAction',
                          event.target
                            .value as
                            typeof draft.requiresAction
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      <option value="yes">
                        Sim
                      </option>
                      <option value="no">
                        Não
                      </option>
                      <option value="uncertain">
                        Incerto
                      </option>
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-xs font-medium text-foreground-600">
                      Tipo de ação
                    </span>
                    <select
                      value={
                        draft.actionType
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'actionType',
                          event.target
                            .value as
                            typeof draft.actionType
                        )
                      }
                      className={
                        selectClass
                      }
                      disabled={
                        draft.requiresAction ===
                        'no'
                      }
                    >
                      {actionTypeOptions.map(
                        ([
                          value,
                          label,
                        ]) => (
                          <option
                            key={value}
                            value={value}
                          >
                            {label}
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label className="space-y-1 md:col-span-2">
                    <span className="text-xs font-medium text-foreground-600">
                      Providência
                    </span>
                    <textarea
                      value={
                        draft.suggestedAction
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'suggestedAction',
                          event.target
                            .value
                        )
                      }
                      disabled={
                        draft.requiresAction ===
                        'no'
                      }
                      rows={2}
                      className={
                        inputClass
                      }
                    />
                  </label>

                  <label className="space-y-1 md:col-span-2">
                    <span className="text-xs font-medium text-foreground-600">
                      Parte afetada
                    </span>
                    <input
                      value={
                        draft.affectedParty
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'affectedParty',
                          event.target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </label>
                </div>

                <div className="space-y-3 rounded-xl border border-red-100 bg-red-50/40 p-3">
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-red-700">
                      Existe prazo?
                    </span>
                    <select
                      value={
                        draft.deadlineSource
                      }
                      onChange={(
                        event
                      ) =>
                        updateDraft(
                          'deadlineSource',
                          event.target
                            .value as
                            typeof draft.deadlineSource
                        )
                      }
                      className={
                        selectClass
                      }
                    >
                      <option value="unknown">
                        Não identificado
                      </option>
                      <option value="explicit">
                        Expresso na publicação
                      </option>
                      <option value="inferred">
                        Inferido juridicamente
                      </option>
                    </select>
                  </label>

                  {draft.deadlineSource !==
                    'unknown' && (
                    <>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <label className="space-y-1">
                          <span className="text-xs font-medium text-red-700">
                            Quantidade
                          </span>
                          <input
                            type="number"
                            min="1"
                            max="8760"
                            value={
                              draft.deadlineValue
                            }
                            onChange={(
                              event
                            ) =>
                              updateDraft(
                                'deadlineValue',
                                event
                                  .target
                                  .value
                              )
                            }
                            className={
                              inputClass
                            }
                          />
                        </label>

                        <label className="space-y-1">
                          <span className="text-xs font-medium text-red-700">
                            Unidade
                          </span>
                          <select
                            value={
                              draft.deadlineUnit
                            }
                            onChange={(
                              event
                            ) =>
                              updateDraft(
                                'deadlineUnit',
                                event
                                  .target
                                  .value as
                                  typeof draft.deadlineUnit
                              )
                            }
                            className={
                              selectClass
                            }
                          >
                            <option value="">
                              Selecione
                            </option>
                            <option value="days">
                              Dias
                            </option>
                            <option value="hours">
                              Horas
                            </option>
                          </select>
                        </label>

                        <label className="space-y-1">
                          <span className="text-xs font-medium text-red-700">
                            Contagem
                          </span>
                          <select
                            value={
                              draft.deadlineCountType
                            }
                            onChange={(
                              event
                            ) =>
                              updateDraft(
                                'deadlineCountType',
                                event
                                  .target
                                  .value as
                                  typeof draft.deadlineCountType
                              )
                            }
                            className={
                              selectClass
                            }
                          >
                            <option value="">
                              Selecione
                            </option>
                            <option value="business_days">
                              Dias úteis
                            </option>
                            <option value="calendar_days">
                              Dias corridos
                            </option>
                            <option value="unspecified">
                              Não especificado
                            </option>
                          </select>
                        </label>
                      </div>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-red-700">
                          Fundamento jurídico
                        </span>
                        <input
                          value={
                            draft.legalBasis
                          }
                          onChange={(
                            event
                          ) =>
                            updateDraft(
                              'legalBasis',
                              event
                                .target
                                .value
                            )
                          }
                          className={
                            inputClass
                          }
                        />
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-red-700">
                          Trecho que fundamenta o prazo
                        </span>
                        <textarea
                          value={
                            draft.deadlineEvidenceQuote
                          }
                          onChange={(
                            event
                          ) =>
                            updateDraft(
                              'deadlineEvidenceQuote',
                              event
                                .target
                                .value
                            )
                          }
                          rows={3}
                          className={
                            inputClass
                          }
                        />
                      </label>

                      <p className="text-[11px] leading-5 text-red-700">
                        O valor acima ainda não é a data final. A data concreta somente será registrada em uma etapa separada de confirmação.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            <label className="block space-y-1">
              <span className="text-xs font-medium text-foreground-600">
                Observações da revisão
                {decision ===
                  'rejected'
                  ? ' — obrigatórias'
                  : ''}
              </span>
              <textarea
                value={notes}
                onChange={(event) =>
                  setNotes(
                    event.target.value
                  )
                }
                rows={3}
                className={
                  inputClass
                }
                placeholder={
                  decision ===
                  'rejected'
                    ? 'Explique por que a análise deve ser rejeitada.'
                    : 'Registre contexto ou fundamento adicional, se necessário.'
                }
              />
            </label>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  void saveReview()
                }
                disabled={
                  isSaving
                }
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving
                  ? 'Registrando...'
                  : decision ===
                      'accepted'
                    ? 'Aceitar e operacionalizar'
                    : decision ===
                        'corrected'
                      ? 'Corrigir e operacionalizar'
                      : 'Rejeitar análise'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setReviewContext(
                    null
                  );
                  setDraft(null);
                  setError(null);
                }}
                disabled={
                  isSaving
                }
                className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
    </section>
  );
}